import {
    useRef,
    useCallback,
    useEffect,
    useState,
    type RefObject,
} from 'react';

/** Minimum drag distance before a gesture is treated as a scroll (not a click). */
const DRAG_THRESHOLD_PX = 5;

/** Recent pointer samples used to estimate release velocity. */
const VELOCITY_SAMPLE_WINDOW_MS = 100;

/** Ignore samples with near-zero duration when estimating velocity. */
const MIN_VELOCITY_SAMPLE_DT_MS = 1;

/** Minimum release velocity (px/ms) required to start inertial scrolling. */
const MIN_MOMENTUM_VELOCITY_PX_PER_MS = 0.05;

/** Stop inertial scrolling once velocity falls below this threshold (px/ms). */
const MOMENTUM_STOP_VELOCITY_PX_PER_MS = 0.01;

/**
 * Exponential friction coefficient for time-based deceleration.
 * Higher values stop sooner; tuned for a short, natural desktop flick.
 */
const MOMENTUM_FRICTION_PER_MS = 0.004;

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

interface VelocitySample {
    timeMs: number;
    clientX: number;
}

interface UseDragScrollOptions {
    /** Sensitivity multiplier for scroll speed (default: 1). */
    sensitivity?: number;
    /** Optional external ref to use instead of creating a new one. */
    externalRef?: RefObject<HTMLElement | null>;
    /**
     * When true, continue scrolling with decelerating inertia after a mouse flick.
     * Opt-in so other drag surfaces keep stop-on-release behaviour.
     */
    momentum?: boolean;
}

interface UseDragScrollReturn {
    /** Ref to attach to the scrollable container. */
    scrollRef: RefObject<HTMLElement | null>;
    /** Whether the user is currently dragging. */
    isDragging: boolean;
}

/**
 * Returns true when the user has requested reduced motion.
 */
const prefersReducedMotion = (): boolean => {
    if (
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
    ) {
        return false;
    }

    return window.matchMedia(REDUCED_MOTION_QUERY).matches;
};

/**
 * Estimates horizontal pointer velocity (px/ms) from recent samples.
 */
const estimateVelocityPxPerMs = (samples: VelocitySample[]): number => {
    const first = samples[0];
    const last = samples[samples.length - 1];
    if (!first || !last || samples.length < 2) {
        return 0;
    }

    const dt = last.timeMs - first.timeMs;
    if (dt < MIN_VELOCITY_SAMPLE_DT_MS) {
        return 0;
    }

    return (last.clientX - first.clientX) / dt;
};

/**
 * Clamps scrollLeft to the element's scrollable range.
 */
const clampScrollLeft = (
    element: HTMLElement,
    nextScrollLeft: number,
): number => {
    const maxScrollLeft = Math.max(
        0,
        element.scrollWidth - element.clientWidth,
    );
    return Math.min(maxScrollLeft, Math.max(0, nextScrollLeft));
};

/**
 * Hook that enables horizontal mouse drag-to-scroll on a container element.
 * Optionally continues with decelerating inertia after release when `momentum` is enabled.
 *
 * Only mouse input is handled. Touch and pen fall through to native scrolling, so
 * `handlePointerDown` returns early for them and no other handler ever owns their pointer.
 *
 * ## Gesture lifecycle
 *
 * A press starts a *candidate* gesture that is still assumed to be a click:
 *
 * 1. `pointerdown` on the container — records the origin, marks the pointer as owned, and
 *    applies the grab styles. The pointer is deliberately left uncaptured.
 * 2. `pointermove` below {@link DRAG_THRESHOLD_PX} — scrolls, but the gesture is still a
 *    candidate click.
 * 3. First `pointermove` past the threshold — the gesture is now a drag. The container
 *    captures the pointer and arms click suppression.
 * 4. `pointerup` / `pointercancel` — releases capture, clears the grab styles, and starts
 *    momentum if enabled and the release was fast enough.
 *
 * **Capture must not happen on press.** Pointer capture retargets subsequent pointer events
 * to the capturing element, so the browser resolves the following `click` against this
 * container rather than the element that was pressed. Every link and button inside the
 * container would stop responding. Past the threshold the retarget is wanted: it keeps the
 * moves flowing when the pointer leaves the container mid-drag.
 *
 * ## Listener placement
 *
 * Split deliberately, and the split is load-bearing:
 *
 * - **Container** — `pointerdown` (scopes the gesture to this element), `lostpointercapture`
 *   and `keydown` (dispatched at the container), `click` (capture phase, to suppress the
 *   click that trails a drag), and `dragstart` (native link-dragging steals pointermove).
 * - **Window** — `pointermove`, `pointerup`, `pointercancel`. Below the threshold there is
 *   no capture, so a pointer that leaves the container would otherwise strand the gesture:
 *   no further moves, no release, and the grab styles left behind. Each of these returns
 *   immediately unless this hook owns the pointer, so idle mouse movement is a ref read.
 *
 * ## Click suppression
 *
 * After a real drag the browser still fires a `click`, which would navigate the link the
 * pointer happens to be over. `hasDraggedRef` arms the container's capture-phase `click`
 * handler to swallow exactly that one. It must survive from `pointerup` to `click`, so it is
 * not cleared on release. A drag ending outside the container produces no `click` here at
 * all, so the flag is also cleared by anything that starts a fresh interaction: any
 * `pointerdown` (including the touch and non-primary presses this hook ignores for
 * dragging) and the `keydown` that precedes a keyboard activation's `click`.
 *
 * @param options - Configuration options
 * @returns Object containing the ref to attach and dragging state
 */
export const useDragScroll = (
    options: UseDragScrollOptions = {},
): UseDragScrollReturn => {
    const { sensitivity = 1, externalRef, momentum = false } = options;
    const internalRef = useRef<HTMLElement>(null);
    const scrollRef = externalRef ?? internalRef;

    // Gesture state. Refs rather than state: these change on every pointermove and
    // are read inside listeners, so a re-render per frame would be wasteful and the
    // listeners would need rebinding to see fresh values.

    /** A mouse gesture this hook owns is in progress, drag or candidate click. */
    const isDraggingRef = useRef(false);
    /** Pointer id of that gesture; other pointers are ignored until it ends. */
    const activePointerIdRef = useRef<number | null>(null);
    /** `clientX` at press, the origin distance is measured from. */
    const startXRef = useRef(0);
    /** `scrollLeft` at press, the origin scroll offset is applied to. */
    const scrollLeftRef = useRef(0);
    /** The gesture passed the drag threshold, so the trailing click is swallowed. */
    const hasDraggedRef = useRef(false);
    /** Recent pointer positions, trimmed to the velocity window, for release speed. */
    const velocitySamplesRef = useRef<VelocitySample[]>([]);

    // Momentum animation state, live only between release and the animation stopping.

    /** Handle of the in-flight frame request, or null when not animating. */
    const momentumRafRef = useRef<number | null>(null);
    /** Current inertial scroll velocity in px/ms, decaying each frame. */
    const momentumVelocityRef = useRef(0);
    /** Timestamp of the previous frame, or null before the first one. */
    const momentumLastTimeRef = useRef<number | null>(null);

    /** Remove the grab cursor and selection lock applied for the duration of a gesture. */
    const clearDragStyles = useCallback((container: HTMLElement | null) => {
        if (!container) {
            return;
        }

        container.style.cursor = '';
        container.style.userSelect = '';
    }, []);

    /** Stop any in-flight inertial scroll and reset its animation state. */
    const cancelMomentum = useCallback(() => {
        if (momentumRafRef.current !== null) {
            cancelAnimationFrame(momentumRafRef.current);
            momentumRafRef.current = null;
        }
        momentumVelocityRef.current = 0;
        momentumLastTimeRef.current = null;
    }, []);

    /** Record a pointer position and drop samples older than the velocity window. */
    const pushVelocitySample = useCallback(
        (clientX: number, timeMs: number) => {
            const samples = velocitySamplesRef.current;
            samples.push({ clientX, timeMs });

            const windowStart = timeMs - VELOCITY_SAMPLE_WINDOW_MS;
            while (samples.length > 0) {
                const oldestSample = samples[0];
                if (!oldestSample || oldestSample.timeMs >= windowStart) {
                    break;
                }
                samples.shift();
            }
        },
        [],
    );

    /**
     * Begin inertial scrolling from a release velocity, decaying it each frame until it
     * falls below the stop threshold or the scroll range runs out. No-op unless `momentum`
     * is enabled, and skipped entirely when the reader prefers reduced motion.
     */
    const startMomentum = useCallback(
        (releaseVelocityPxPerMs: number) => {
            const container = scrollRef.current;
            if (!container || !momentum || prefersReducedMotion()) {
                return;
            }

            // Pointer velocity is opposite to scrollLeft direction.
            const scrollVelocity = -releaseVelocityPxPerMs * sensitivity;
            if (Math.abs(scrollVelocity) < MIN_MOMENTUM_VELOCITY_PX_PER_MS) {
                return;
            }

            cancelMomentum();
            momentumVelocityRef.current = scrollVelocity;
            momentumLastTimeRef.current = null;

            const animate = (timeMs: number) => {
                const element = scrollRef.current;
                if (!element) {
                    cancelMomentum();
                    return;
                }

                const lastTimeMs = momentumLastTimeRef.current;
                momentumLastTimeRef.current = timeMs;
                const dtMs =
                    lastTimeMs === null ? 16 : Math.max(0, timeMs - lastTimeMs);
                let velocity = momentumVelocityRef.current;

                if (dtMs > 0) {
                    const unclampedScrollLeft =
                        element.scrollLeft + velocity * dtMs;
                    const nextScrollLeft = clampScrollLeft(
                        element,
                        unclampedScrollLeft,
                    );
                    element.scrollLeft = nextScrollLeft;

                    const maxScrollLeft = Math.max(
                        0,
                        element.scrollWidth - element.clientWidth,
                    );
                    const hitStartBoundary =
                        unclampedScrollLeft < 0 && nextScrollLeft <= 0;
                    const hitEndBoundary =
                        unclampedScrollLeft > maxScrollLeft &&
                        nextScrollLeft >= maxScrollLeft;

                    if (hitStartBoundary || hitEndBoundary) {
                        cancelMomentum();
                        return;
                    }

                    velocity *= Math.exp(-MOMENTUM_FRICTION_PER_MS * dtMs);
                    momentumVelocityRef.current = velocity;
                }

                if (Math.abs(velocity) < MOMENTUM_STOP_VELOCITY_PX_PER_MS) {
                    cancelMomentum();
                    return;
                }

                momentumRafRef.current = requestAnimationFrame(animate);
            };

            momentumRafRef.current = requestAnimationFrame(animate);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cancelMomentum, momentum, sensitivity],
    );

    /**
     * Finish the gesture owning `pointerId`: drop ownership, clear the grab styles, and
     * release capture if the gesture took it. Ignores pointers this hook does not own, so
     * it is safe to call from the window listeners and from `lostpointercapture` alike.
     */
    const endDrag = useCallback(
        (pointerId: number, shouldStartMomentum: boolean) => {
            const container = scrollRef.current;
            if (
                !isDraggingRef.current ||
                activePointerIdRef.current !== pointerId
            ) {
                return;
            }

            isDraggingRef.current = false;
            activePointerIdRef.current = null;
            clearDragStyles(container);

            if (container?.hasPointerCapture?.(pointerId)) {
                try {
                    container.releasePointerCapture(pointerId);
                } catch {
                    // Capture may already have been released by the browser.
                }
            }

            if (!shouldStartMomentum) {
                velocitySamplesRef.current = [];
                return;
            }

            const releaseVelocity = estimateVelocityPxPerMs(
                velocitySamplesRef.current,
            );
            velocitySamplesRef.current = [];
            startMomentum(releaseVelocity);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [clearDragStyles, startMomentum],
    );

    /** Start a candidate gesture. Left uncaptured so a plain press stays a click. */
    const handlePointerDown = useCallback(
        (event: PointerEvent) => {
            // Any fresh press starts a new gesture, so the previous one can no
            // longer suppress a click. Reset before the mouse guard below: a
            // touch tap or a right-click is ignored for dragging but still ends
            // whatever came before it.
            hasDraggedRef.current = false;

            if (event.pointerType !== 'mouse' || event.button !== 0) {
                return;
            }

            const container = scrollRef.current;
            if (!container) {
                return;
            }

            cancelMomentum();

            isDraggingRef.current = true;
            activePointerIdRef.current = event.pointerId;
            startXRef.current = event.clientX;
            scrollLeftRef.current = container.scrollLeft;
            velocitySamplesRef.current = [];
            pushVelocitySample(event.clientX, performance.now());

            container.style.cursor = 'grabbing';
            container.style.userSelect = 'none';
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [cancelMomentum, pushVelocitySample],
    );

    /** Scroll with the pointer, promoting the gesture to a captured drag past the threshold. */
    const handlePointerMove = useCallback(
        (event: PointerEvent) => {
            if (
                !isDraggingRef.current ||
                activePointerIdRef.current !== event.pointerId
            ) {
                return;
            }

            const container = scrollRef.current;
            if (!container) {
                return;
            }

            event.preventDefault();
            const walk = (event.clientX - startXRef.current) * sensitivity;

            if (Math.abs(walk) > DRAG_THRESHOLD_PX) {
                // Capture only once the gesture is a real drag. Capturing on
                // pointerdown retargets the pointer events to this container,
                // so the browser resolves the following click against the
                // container instead of the link the reader pressed — every
                // pill and card in the rail stops navigating. By this point
                // the gesture is a scroll, not a click, so the retarget is
                // what we want: it keeps the moves coming if the pointer
                // leaves the rail mid-drag.
                if (!hasDraggedRef.current) {
                    try {
                        container.setPointerCapture(event.pointerId);
                    } catch {
                        // Some test environments do not implement pointer capture.
                    }
                }
                hasDraggedRef.current = true;
            }

            pushVelocitySample(event.clientX, performance.now());
            container.scrollLeft = scrollLeftRef.current - walk;
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pushVelocitySample, sensitivity],
    );

    /** Release: end the gesture and let it carry into momentum. */
    const handlePointerUp = useCallback(
        (event: PointerEvent) => {
            endDrag(event.pointerId, true);
        },
        [endDrag],
    );

    /** Interrupted gesture: end it without momentum, since there was no deliberate release. */
    const handlePointerCancel = useCallback(
        (event: PointerEvent) => {
            endDrag(event.pointerId, false);
        },
        [endDrag],
    );

    /** The browser can revoke capture without a matching pointerup; end the gesture cleanly. */
    const handleLostPointerCapture = useCallback(
        (event: PointerEvent) => {
            // Browser can revoke capture without a matching pointerup; end cleanly.
            endDrag(event.pointerId, true);
        },
        [endDrag],
    );

    /** Prevent native browser link-drag (drag-to-bookmark), which steals pointermove events. */
    const handleDragStart = useCallback((event: DragEvent) => {
        event.preventDefault();
    }, []);

    /** Suppress the click that browsers fire after a meaningful drag. */
    const handleClick = useCallback((event: MouseEvent) => {
        if (hasDraggedRef.current) {
            event.preventDefault();
            event.stopPropagation();
            hasDraggedRef.current = false;
        }
    }, []);

    /**
     * A drag that ends outside the rail never produces a click here, so the
     * suppression flag can outlive it. Keyboard activation fires `click` with no
     * preceding `pointerdown` to clear the flag, so clear it on the keydown that
     * precedes the click instead.
     */
    const handleKeyDown = useCallback(() => {
        hasDraggedRef.current = false;
    }, []);

    // Sync the live DOM node into state so listeners rebind when the scroll
    // container remounts (e.g. ProductsFilterBar Products/Brands toggle).
    const [element, setElement] = useState<HTMLElement | null>(null);
    useEffect(() => {
        setElement(scrollRef.current);
    });

    useEffect(() => {
        const container = element;
        if (!container) {
            return;
        }

        container.addEventListener('pointerdown', handlePointerDown);
        container.addEventListener(
            'lostpointercapture',
            handleLostPointerCapture,
        );
        container.addEventListener('click', handleClick, true);
        container.addEventListener('keydown', handleKeyDown, true);
        container.addEventListener('dragstart', handleDragStart);

        // The rest of the gesture is tracked on the window. Below the drag
        // threshold there is no pointer capture yet, so a pointer that leaves
        // the rail would otherwise strand the drag: no more moves, no pointerup,
        // and the grab cursor left behind. Each handler returns immediately
        // unless this hook owns the pointer, so idle mouse movement is free.
        window.addEventListener('pointermove', handlePointerMove);
        window.addEventListener('pointerup', handlePointerUp);
        window.addEventListener('pointercancel', handlePointerCancel);

        return () => {
            container.removeEventListener('pointerdown', handlePointerDown);
            container.removeEventListener(
                'lostpointercapture',
                handleLostPointerCapture,
            );
            container.removeEventListener('click', handleClick, true);
            container.removeEventListener('keydown', handleKeyDown, true);
            container.removeEventListener('dragstart', handleDragStart);
            window.removeEventListener('pointermove', handlePointerMove);
            window.removeEventListener('pointerup', handlePointerUp);
            window.removeEventListener('pointercancel', handlePointerCancel);
            clearDragStyles(container);
            cancelMomentum();
        };
    }, [
        element,
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handlePointerCancel,
        handleLostPointerCapture,
        handleClick,
        handleKeyDown,
        handleDragStart,
        clearDragStyles,
        cancelMomentum,
    ]);

    return {
        scrollRef,
        isDragging: isDraggingRef.current,
    };
};
